import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

// Utility functions (defined first to avoid no-use-before-define)
function formatSuppressionType(type) {
  const typeLabels = {
    bounces: "Bounce",
    blocks: "Block",
    invalid_emails: "Invalid Email",
    spam_reports: "Spam Report",
    global_unsubscribes: "Global Unsubscribe",
  };
  return typeLabels[type] || type.replace(/_/g, " ");
}

function isAccountSkipped(accountData) {
  if (!accountData) return true;
  const entries = Object.entries(accountData);
  return entries.length > 0 && entries[0]?.[1]?.skipped;
}

function areAllAccountsSkipped(results) {
  if (!results) return false;
  return isAccountSkipped(results.v2_account) && isAccountSkipped(results.v1_account);
}

/**
 * SendgridSuppressionCard - Displays SendGrid suppression status for a user
 *
 * Auto-fetches suppression status on mount and provides a button to clear
 * all suppressions. After clearing, re-fetches to show updated status.
 */
function SendgridSuppressionCard({ email, userName }) {
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchSuppressionStatus = useCallback(async () => {
    if (!email) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/admin/api/sendgrid_suppressions/check?email=${encodeURIComponent(email)}`, {
        method: "GET",
        headers: new Headers(authenticatedHeadersHash()),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchSuppressionStatus();
  }, [fetchSuppressionStatus]);

  const handleClearClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmClear = async () => {
    setShowConfirmModal(false);
    setIsClearing(true);
    setError(null);

    try {
      const response = await fetch("/admin/api/sendgrid_suppressions/clear", {
        method: "POST",
        headers: new Headers({
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Re-fetch to show updated status
      await fetchSuppressionStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsClearing(false);
    }
  };

  if (!email) {
    return null;
  }

  const hasSuppressions = results?.summary?.total_found > 0;
  const allSkipped = areAllAccountsSkipped(results);

  // Determine display state
  let borderClass;
  let iconClass;
  let statusIcon;
  let statusTitle;
  let statusMessage;

  if (allSkipped) {
    borderClass = "border-secondary";
    iconClass = "text-secondary";
    statusIcon = "fa-question-circle";
    statusTitle = "Not Configured";
    statusMessage = "SendGrid API keys are not configured. Unable to check suppression status.";
  } else if (hasSuppressions) {
    borderClass = "border-warning";
    iconClass = "text-warning";
    statusIcon = "fa-exclamation-triangle";
    statusTitle = "Suppressions Found";
    statusMessage = `Found ${results.summary.total_found} suppression(s) that may be blocking email delivery`;
  } else {
    borderClass = "border-success";
    iconClass = "text-success";
    statusIcon = "fa-check-circle";
    statusTitle = "No Suppressions";
    statusMessage = "This email address is not blocked by SendGrid";
  }

  return (
    <div className={`card mt-4 ${borderClass}`} style={{ borderLeftWidth: "4px" }}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fa fa-envelope-open me-2" />
          SendGrid Suppression Status
        </h5>
        {hasSuppressions && !isLoading && !isClearing && (
          <button type="button" className="btn btn-warning btn-sm" onClick={handleClearClick}>
            <i className="fa fa-eraser me-1" />
            Clear All Suppressions
          </button>
        )}
      </div>
      <div className="card-body">
        {isLoading && (
          <div className="text-center p-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Checking suppression status...</span>
          </div>
        )}

        {isClearing && (
          <div className="text-center p-3">
            <div className="spinner-border spinner-border-sm text-warning" role="status">
              <span className="visually-hidden">Clearing...</span>
            </div>
            <span className="ms-2">Clearing suppressions...</span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mb-0" role="alert">
            <i className="fa fa-exclamation-circle me-2" />
            {error}
          </div>
        )}

        {!isLoading && !isClearing && !error && results && (
          <>
            <div className="d-flex align-items-center mb-3">
              <i className={`fa ${statusIcon} ${iconClass} fs-4 me-3`} />
              <div>
                <strong>{statusTitle}</strong>
                <div className="text-muted small">{statusMessage}</div>
              </div>
            </div>

            {!allSkipped && (
              <div className="row">
                <div className="col-md-6">
                  <AccountSummary title="V2 Account" data={results.v2_account} />
                </div>
                <div className="col-md-6">
                  <AccountSummary title="V1 Account" data={results.v1_account} />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmClearModal
        show={showConfirmModal}
        email={email}
        userName={userName}
        onConfirm={handleConfirmClear}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}

function AccountSummary({ title, data }) {
  if (!data) return null;

  const entries = Object.entries(data);
  const firstEntry = entries[0]?.[1];

  if (firstEntry?.skipped) {
    return (
      <div className="mb-2">
        <small className="text-muted fw-bold">{title}</small>
        <div className="text-muted small">
          <span className="badge bg-secondary me-1">Skipped</span>
          {firstEntry.error}
        </div>
      </div>
    );
  }

  const found = entries.filter(([, result]) => result.found);
  const notFound = entries.filter(([, result]) => !result.found && !result.error);

  return (
    <div className="mb-2">
      <small className="text-muted fw-bold">{title}</small>
      {found.length > 0 ? (
        <ul className="list-unstyled mb-0 mt-1">
          {found.map(([type, result]) => (
            <li key={type} className="small">
              <span className="badge bg-warning text-dark me-1">{formatSuppressionType(type)}</span>
              {result.reason && <span className="text-muted">{result.reason}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-muted small">
          <i className="fa fa-check text-success me-1" />
          No suppressions ({notFound.length} types checked)
        </div>
      )}
    </div>
  );
}

function ConfirmClearModal({ show, email, userName, onConfirm, onCancel }) {
  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-exclamation-triangle text-warning me-2" />
          Confirm Suppression Clear
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          You are about to clear <strong>all</strong> email suppressions for:
        </p>
        <p className="text-center">
          {userName && <strong className="d-block">{userName}</strong>}
          <code className="fs-5">{email}</code>
        </p>
        <p>This will remove the email from:</p>
        <ul>
          <li>
            <strong>Bounces</strong> - Delivery failures
          </li>
          <li>
            <strong>Blocks</strong> - Administrative blocks
          </li>
          <li>
            <strong>Invalid emails</strong> - Format issues
          </li>
          <li>
            <strong>Spam reports</strong> - User marked as spam
          </li>
          <li>
            <strong>Global unsubscribes</strong> - User opted out of all emails
          </li>
        </ul>
        <div className="alert alert-warning mb-0">
          <i className="fa fa-info-circle me-2" />
          <strong>Only do this if the user has explicitly requested to receive emails again.</strong>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-warning" onClick={onConfirm}>
          <i className="fa fa-eraser me-1" />
          Yes, Clear All Suppressions
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default SendgridSuppressionCard;
